import React, { useState, useCallback, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import GenericEntityFlowEditGeneralDataCard from './GenericEntityFlowEditGeneralDataCard';
import GenericEntityFlowEditStatesContainer from './States/GenericEntityFlowEditStatesContainer';
import GenericEntityFlowEditTransitionsContainer from './Transitions/GenericEntityFlowEditTransitionsContainer';
import { GENERIC_ENTITY_FLOW_QUERY } from '../list/queries';
import { UPDATE_GENERIC_ENTITY_FLOW } from '../list/mutations';
import { GENERIC_ENTITY_FLOW_CHANGED } from '../list/subscriptions';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useReactiveVar } from '@apollo/client';
import { useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { get } from '@totalsoft/change-tracking-react';
import { shape, required } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useToast } from 'hooks/toasts/useToast';
import { Card } from '@totalsoft/rocket-ui';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { getInt } from 'utils/functions';
import { emptyString } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { isDirty } from '@totalsoft/change-tracking';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { entityContextVar } from './variables';

export const genericEntityFlowValidator = shape({ flowCode: required, flowName: required });

const newGenericFlow = {
  id: undefined,
  siteId: 0,
  flowCode: emptyString,
  flowName: emptyString,
  freeTxt: emptyString,
  preConditionProcedure: emptyString,
  postConditionProcedure: emptyString,
  enabledYN: true,
  mainFlow: false,
  context: emptyString
};

const GenericEntityFlowEditContainer = ({ onRedirect }) => {
  const match = useRouteMatch();
  const flowId = match.params.id && getInt(match.params.id);
  const [t] = useTranslation();
  const [validation, validate] = useDirtyFieldValidation(genericEntityFlowValidator);
  const [headerLens, headerDirtyInfo, resetHeader] = useChangeTrackingLens();
  const addToast = useToast();
  const [headerSaving, setHeaderSaving] = useState(false);
  const genericEntityFlow = headerLens ? headerLens |> get : null;

  const [genericEntityFlowStatesLens, statesDirtyInfo, resetStates] = useChangeTrackingLens([]);
  const [transitionsLens, transitionsDirtyInfo, resetTransitions] = useChangeTrackingLens({});

  const entityContext = useReactiveVar(entityContextVar);

  useEffect(() => {
    if (flowId === undefined) resetHeader({ ...newGenericFlow, context: entityContext });
  }, [match.params.id, entityContext, flowId, resetHeader]);

  useEffect(() => {
    if (genericEntityFlow) validate(genericEntityFlow, headerDirtyInfo);
  }, [headerDirtyInfo, validate, genericEntityFlow]);

  const { loading } = useQuery(GENERIC_ENTITY_FLOW_QUERY, {
    variables: { flowId: flowId },
    skip: !flowId,
    onCompleted: data => {
      resetHeader({ ...data.genericEntityFlow });
    }
  });

  useSubscription(GENERIC_ENTITY_FLOW_CHANGED, {
    onData: ({ data: { data }, client }) => {
      setHeaderSaving(false);
      if (data.genericEntityFlowChanged.mainFlowExists) {
        addToast(t('GenericEntityFlow.MainFlowExists'), 'error');
        resetHeader();
        return;
      }
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityFlows' });
      onRedirect(data.genericEntityFlowChanged.genericEntityFlow.id);
      addToast(t('GenericEntityFlow.GenericEntityFlowSaved'), 'success');
    }
  });

  const [updateGenericEntityFlow] = useMutation(UPDATE_GENERIC_ENTITY_FLOW, {
    onError: () => {
      setHeaderSaving(false);
    }
  });

  const onSaveGenericEntityFlow = useCallback(
    item => {
      const genericEntityFlow = item |> get;
      const input = {
        id: genericEntityFlow.id,
        name: genericEntityFlow.name,
        siteId: genericEntityFlow.siteId,
        flowCode: genericEntityFlow.flowCode,
        flowName: genericEntityFlow.flowName,
        freeTxt: genericEntityFlow.freeTxt,
        enabledYN: genericEntityFlow.enabledYN,
        context: genericEntityFlow.context,
        mainFlow: genericEntityFlow.mainFlow,
        preConditionProcedure: genericEntityFlow.preConditionProcedure,
        postConditionProcedure: genericEntityFlow.postConditionProcedure
      };

      if (!validate(input)) return;
      updateGenericEntityFlow({ variables: { input } });
      setHeaderSaving(true);
    },
    [updateGenericEntityFlow, validate]
  );

  if (flowId && (!(headerLens.id |> get) || loading))
    return (
      <Card icon={PlaylistAddCheckIcon}>
        <WrappedFakeText />
      </Card>
    );

  return (
    <>
      <GenericEntityFlowEditGeneralDataCard
        genericEntityFlowLens={headerLens}
        validation={validation}
        onSave={onSaveGenericEntityFlow}
        isDirty={isDirty(headerDirtyInfo)}
        headerSaving={headerSaving}
      />
      {(headerLens.id |> get) && (
        <>
          <GenericEntityFlowEditStatesContainer
            genericEntityFlowId={flowId}
            genericEntityFlowStatesLens={genericEntityFlowStatesLens}
            statesDirtyInfo={statesDirtyInfo}
            resetStates={resetStates}
            transitionsLens={transitionsLens}
          />
          <GenericEntityFlowEditTransitionsContainer
            flowId={flowId}
            transitionsLens={transitionsLens}
            transitionsDirtyInfo={transitionsDirtyInfo}
            resetTransitions={resetTransitions}
          />
        </>
      )}
    </>
  );
};

GenericEntityFlowEditContainer.propTypes = {
  onRedirect: PropTypes.func
};

export default GenericEntityFlowEditContainer;
